import { Quote, ExternalLink } from "lucide-react";
import { container, sectionPad, label, cardShadow } from "@/lib/ui";
import { StarRating } from "@/components/StarRating";
import { useReviews } from "@/hooks/useReviews";

export function ReviewsSection() {
  const { reviews, rating, url, source } = useReviews();
  const display = reviews.slice(0, 3);

  return (
    <section className={`${sectionPad} bg-neutralbg`} data-testid="reviews-section">
      <div className={container}>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <span className={label}>Reviews</span>
            <h2 className="mt-3 font-heading text-3xl lg:text-4xl font-bold tracking-tight text-navy">
              What our customers say
            </h2>
          </div>
          <div className="flex items-center gap-3 rounded-2xl bg-white px-5 py-3 shadow-sm border border-gray-100">
            <StarRating value={Math.round(rating)} />
            <span className="text-sm font-semibold text-navy">{Number(rating).toFixed(1)} · Google Reviews</span>
          </div>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {display.map((r, i) => (
            <figure key={i} className={`flex flex-col rounded-2xl bg-white p-7 border border-gray-100 ${cardShadow}`} data-testid={`review-${i}`}>
              <Quote className="w-8 h-8 text-brandgreen/30" />
              <StarRating value={r.rating} size="w-4 h-4" className="mt-3" />
              <blockquote className="mt-4 text-sm text-gray-700 leading-relaxed flex-1">"{r.text}"</blockquote>
              <figcaption className="mt-5 flex items-center gap-3">
                {r.photo ? (
                  <img src={r.photo} alt="" className="w-10 h-10 rounded-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <span className="grid place-items-center w-10 h-10 rounded-full bg-navy text-white font-heading font-semibold">{r.name.charAt(0)}</span>
                )}
                <span>
                  <span className="block font-semibold text-navy text-sm">{r.name}</span>
                  <span className="block text-xs text-gray-400">{r.date} · Google</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>

        {url ? (
          <div className="mt-8 text-center">
            <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brandgreen-dark hover:text-brandgreen transition-colors" data-testid="reviews-google-link">
              See all our reviews on Google <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        ) : (
          <p className="mt-8 text-center text-sm text-gray-500">
            {source === "google" ? "Live from our Google Business Profile." : "More reviews syncing automatically once our Google Business Profile is connected."}
          </p>
        )}
      </div>
    </section>
  );
}
